import { useNavigate } from 'react-router-dom';
import { useRecommendation } from '../context/RecommendationContext';
import { CarbonSavingsChart } from '@system-rec/decision-ui';
import { getOptionDisplayTitle } from '../utils/displayNames';
import { getRecommendedOptionSavings } from '@core/moneyImpact';
import { DebugBundleButton } from '../components/DebugBundleButton';
import { gbp, formatNumber } from '../utils/formatters';
import { IP_NOTICE_MINIMAL } from '../legal/ipNotice';
import { ControlPrecisionSection } from '../components/visualizer/ControlPrecisionSection';
import { SystemComponentVisualizer } from '../components/visualizer';
import type { ThermostatControlType } from '@core/types';
import './DecisionBriefPage.css';

function DecisionBriefPage() {
  const navigate = useNavigate();
  const { recommendationResult, collectedData, activeDocumentId } = useRecommendation();

  const handlePrint = () => {
    window.print();
  };

  if (!recommendationResult) {
    return (
      <div className="decision-brief-page">
        <div className="no-print">
          <button onClick={() => navigate(-1)} className="btn btn-secondary">← Back</button>
        </div>
        <p>No recommendation data available. Please complete the decision flow first.</p>
      </div>
    );
  }

  const { output } = recommendationResult;
  const { carbonSavingsData } = recommendationResult;
  
  // Use normalized output for consistent baseline data
  const normalized = recommendationResult.normalizedOutput;
  const baseline = normalized?.baseline;
  
  const moneyImpact = output.moneyImpact;
  const savingsSummary = moneyImpact ? getRecommendedOptionSavings(moneyImpact) : null;
  const options = output.primary ? [output.primary, ...(output.alternatives || [])] : [];

  return (
    <div className="decision-brief-page">
      {/* Print controls - hidden when printing */}
      <div className="no-print decision-brief-controls">
        <button onClick={() => navigate(-1)} className="btn btn-secondary">← Back</button>
        <button 
          onClick={() => navigate(activeDocumentId ? `/story/presentationPrint?docId=${activeDocumentId}` : '/story/presentationPrint')} 
          className="btn btn-secondary"
        >
          Continue to report
        </button>
        <button onClick={handlePrint} className="btn btn-primary">🖨️ Print / Save PDF</button>
        <DebugBundleButton />
      </div>

      {/* PAGE 1: BASELINE */}
      <div className="brief-page brief-page-1">
        <header className="brief-header">
          <h1>Your Heating System Decision</h1>
          <p className="brief-subtitle">Evidence-based analysis · {new Date().toLocaleDateString('en-GB')}</p>
        </header>

        <section className="baseline-section">
          <h2 className="section-title"><span className="emoji-icon">📊</span> Cost of Doing Nothing</h2>
          <p className="section-intro">
            If you keep your current system without changes, here's what to expect over the next {baseline?.periodYears || 20} years:
          </p>

          {baseline ? (
            <>
              <div className="baseline-kpis">
                <div className="kpi-card">
                  <div className="kpi-label">Annual Cost</div>
                  <div className="kpi-value">{baseline.annualCost !== null ? gbp(baseline.annualCost) : '—'}</div>
                  <div className="kpi-unit">per year</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-label">{baseline.periodYears}-Year Total</div>
                  <div className="kpi-value">{baseline.totalCost !== null ? gbp(baseline.totalCost) : '—'}</div>
                  <div className="kpi-unit">including degradation</div>
                </div>

                {baseline.remainingLifespan !== null && (
                  <div className="kpi-card">
                    <div className="kpi-label">Remaining Lifespan</div>
                    <div className="kpi-value">{baseline.remainingLifespan || '?'}</div>
                    <div className="kpi-unit">years estimated</div>
                  </div>
                )}

                {baseline.replacementsInPeriod !== null && baseline.replacementsInPeriod > 0 && (
                  <div className="kpi-card kpi-warning">
                    <div className="kpi-label">Replacements Needed</div>
                    <div className="kpi-value">{baseline.replacementsInPeriod}</div>
                    <div className="kpi-unit">over {baseline.periodYears} years</div>
                  </div>
                )}
              </div>

              {output.baselineResult?.hydronicsInfo && output.baselineResult.hydronicsInfo.severity !== 'low' && (
                <div className="baseline-warning">
                  <strong>⚠️ Sludge Risk Detected:</strong> {output.baselineResult.hydronicsInfo.severity} contamination
                  may be reducing efficiency by {Math.round((output.baselineResult.hydronicsInfo.efficiencyPenalty || 0) * 100)}%
                  and increasing breakdown risk.
                </div>
              )}

              <div className="baseline-context">
                <h3>What This Means</h3>
                <ul>
                  {baseline.annualCost !== null && (
                    <li>Your current system costs approximately <strong>{gbp(baseline.annualCost)}/year</strong> to run</li>
                  )}
                  {baseline.remainingLifespan !== null && (
                    <li>Expected to last approximately <strong>{baseline.remainingLifespan} more years</strong></li>
                  )}
                  {baseline.replacementsInPeriod !== null && baseline.replacementsInPeriod > 0 && (
                    <li>Will require <strong>{baseline.replacementsInPeriod} replacement(s)</strong> during the analysis period</li>
                  )}
                  {output.baselineResult?.hydronicsInfo && output.baselineResult.hydronicsInfo.severity !== 'low' && (
                    <li>Efficiency is degrading due to <strong>{output.baselineResult.hydronicsInfo.severity} sludge contamination</strong></li>
                  )}
                </ul>
              </div>

              <div className="baseline-assumptions">
                <h4>Assumptions Used</h4>
                <p>
                  System age: <strong>{collectedData?.systemAge} years</strong>
                  {baseline.source && <> · Efficiency: <strong>{baseline.source}</strong></>}
                  {collectedData?.currentFuelType && <> · Fuel: <strong>{collectedData.currentFuelType}</strong></>}
                  {collectedData?.sludgeRisk && collectedData.sludgeRisk !== 'none' &&
                    <> · Sludge risk: <strong>{collectedData.sludgeRisk}</strong></>
                  }
                </p>
              </div>
            </>
          ) : (
            <p className="no-baseline">Baseline calculation not available. Additional system information needed.</p>
          )}
        </section>

        <footer className="brief-footer">
          <p><strong>Important:</strong> This analysis is illustrative and based on typical UK conditions and current energy prices.
          Actual costs will vary based on your usage patterns, energy tariff, and system-specific factors.
          Professional assessment recommended before making final decisions.</p>
        </footer>
      </div>

      {/* PAGE 2: OPTIONS */}
      <div className="brief-page brief-page-2">
        <header className="brief-header">
          <h1>Your Alternative Options</h1>
          <p className="brief-subtitle">Top-ranked systems compared to doing nothing</p>
        </header>

        <section className="options-section">
          {options.slice(0, 4).map((option, index) => {
            const isRecommended = index === 0;
            const delta = option.deltaVsBaseline;
            const displayTitle = getOptionDisplayTitle(option);

            return (
              <div key={option.id} className={`option-brief-card ${isRecommended ? 'recommended' : ''}`}>
                <div className="option-brief-header">
                  <div>
                    <h3 className="option-brief-title">
                      {isRecommended && <span className="recommended-badge">RECOMMENDED</span>}
                      {displayTitle}
                    </h3>
                    <p className="option-brief-subtitle">
                      {option.boilerLabel || option.boiler || 'System'} {option.water && option.water !== 'on_demand' && `+ ${option.water.replace(/_/g, ' ')}`}
                    </p>
                  </div>
                  {delta && (
                    <div className={`option-delta ${delta.totalSaving_gbp > 0 ? 'positive' : 'negative'}`}>
                      <div className="delta-amount">
                        {delta.totalSaving_gbp > 0 ? '' : '-'}
                        {gbp(Math.abs(delta.totalSaving_gbp))}
                      </div>
                      <div className="delta-label">
                        {delta.totalSaving_gbp > 0 ? 'Saves' : 'Costs'} vs baseline
                      </div>
                    </div>
                  )}
                </div>

                <div className="option-brief-content">
                  <div className="option-brief-column">
                    <h4>✓ Strengths</h4>
                    <ul>
                      {option.pros.slice(0, 3).map((pro, i) => (
                        <li key={i}>{pro}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="option-brief-column">
                    <h4>⚠️ Considerations</h4>
                    <ul>
                      {option.cons.slice(0, 3).map((con, i) => (
                        <li key={i}>{con}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="option-brief-column">
                    <h4>📊 Key Metrics</h4>
                    <ul>
                      {option.lcoo && (
                        <>
                          <li>Upfront: {gbp(option.lcoo.upfrontCost_gbp)}</li>
                          <li>Annual: {gbp(option.lcoo.annualizedCostGBP)}/yr</li>
                          <li><strong>Total LCOO: {gbp(option.lcoo.totalCost_gbp)}</strong></li>
                        </>
                      )}
                      {option.longevity && (
                        <li>Lifespan: {option.longevity.expectedLifespanYears.min}–{option.longevity.expectedLifespanYears.max} years</li>
                      )}
                      {delta && delta.breakEvenYears !== null && delta.breakEvenYears > 0 && (
                        <li>Break-even: {delta.breakEvenYears} years</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* System Layout Section - Show primary recommendation's layout */}
        {output.primary?.visualModel && (
          <section className="options-section" style={{ marginTop: '3rem', pageBreakInside: 'avoid' }}>
            <h2 className="section-title"><span className="emoji-icon">🏠</span> System Layout</h2>
            <p className="section-intro">
              Here's what the recommended system physically looks like in your home
            </p>
            <SystemComponentVisualizer
              systemType={output.primary.boiler}
              systemTitle={getOptionDisplayTitle(output.primary)}
              flowRate={collectedData?.flowRate ? parseFloat(collectedData.flowRate) : undefined}
              printMode={true}
              visualModel={output.primary.visualModel}
            />
          </section>
        )}

        {/* Control Precision Section - Show if primary is a gas boiler */}
        {(() => {
          const isGasBoiler = output.primary?.boiler &&
            ['combi', 'system', 'regular', 'storage_combi'].includes(output.primary.boiler);

          if (!isGasBoiler) return null;

          const currentThermostatType = (collectedData?.currentThermostatType || 'mechanical') as ThermostatControlType;
          const supportsLoadComp = true;

          return (
            <section className="options-section" style={{ marginTop: '3rem', pageBreakInside: 'avoid' }}>
              <h2 className="section-title"><span className="emoji-icon">🌡️</span> Control Precision</h2>
              <p className="section-intro">
                The right thermostat can save 10-20% on fuel bills. Here's why precision matters.
              </p>
              <ControlPrecisionSection
                currentThermostatType={currentThermostatType}
                recommendedThermostatType="smart_load_comp"
                boilerSupportsLoadCompensation={supportsLoadComp}
                printMode={true}
              />
            </section>
          );
        })()}

        {/* Money Savings Section - NEW */}
        {savingsSummary && (
          <section className="options-section" style={{ marginTop: '3rem' }}>
            <h2 className="section-title"><span className="emoji-icon">💰</span> Money Savings vs Doing Nothing</h2>
            <p className="section-intro">
              Financial comparison of the recommended option vs keeping your current system over {savingsSummary.horizonYears} years.
            </p>
            
            {/* Savings KPI Cards */}
            <div className="baseline-kpis" style={{ marginBottom: '2rem' }}>
              <div className={`kpi-card ${savingsSummary.annualSaving > 0 ? 'kpi-success' : 'kpi-warning'}`}>
                <div className="kpi-label">Annual Saving</div>
                <div className="kpi-value">
                  {savingsSummary.annualSaving > 0 ? '+' : ''}{gbp(savingsSummary.annualSaving)}
                </div>
                <div className="kpi-unit">per year</div>
              </div>

              <div className={`kpi-card ${savingsSummary.totalSaving > 0 ? 'kpi-success' : 'kpi-warning'}`}>
                <div className="kpi-label">{savingsSummary.horizonYears}-Year Saving</div>
                <div className="kpi-value">
                  {savingsSummary.totalSaving > 0 ? '+' : ''}{gbp(savingsSummary.totalSaving)}
                </div>
                <div className="kpi-unit">total saving</div>
              </div>

              {savingsSummary.paybackYears !== undefined && (
                <div className="kpi-card">
                  <div className="kpi-label">Payback Period</div>
                  <div className="kpi-value">{savingsSummary.paybackYears}</div>
                  <div className="kpi-unit">years</div>
                </div>
              )}
            </div>

            <div style={{
              background: savingsSummary.totalSaving > 0 ? '#d4edda' : '#fff3cd',
              border: `2px solid ${savingsSummary.totalSaving > 0 ? '#28a745' : '#ffc107'}`,
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '1rem'
            }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                color: savingsSummary.totalSaving > 0 ? '#155724' : '#856404', 
                marginBottom: '0.5rem' 
              }}>
                {savingsSummary.totalSaving > 0 ? '💰 Financial Benefit' : '⚠️ Cost Consideration'}
              </h3>
              <p style={{ 
                fontSize: '1rem', 
                color: savingsSummary.totalSaving > 0 ? '#155724' : '#856404', 
                marginBottom: '0.5rem' 
              }}>
                {savingsSummary.totalSaving > 0 ? (
                  <>
                    The recommended system will <strong>save you {gbp(savingsSummary.annualSaving)} per year</strong>,
                    totaling <strong>{gbp(savingsSummary.totalSaving)}</strong> over {savingsSummary.horizonYears} years
                    compared to keeping your current system.
                  </>
                ) : (
                  <>
                    The recommended system will cost <strong>{gbp(Math.abs(savingsSummary.annualSaving))} more per year</strong>,
                    totaling <strong>{gbp(Math.abs(savingsSummary.totalSaving))}</strong> more over {savingsSummary.horizonYears} years
                    compared to keeping your current system.
                  </>
                )}
              </p>
              {savingsSummary.paybackYears !== undefined && savingsSummary.totalSaving > 0 && (
                <p style={{ fontSize: '0.875rem', color: '#155724', margin: 0 }}>
                  <strong>Payback period:</strong> Your initial investment will be recovered in {savingsSummary.paybackYears} years,
                  after which you continue to save money on running costs.
                </p>
              )}
              <p style={{ 
                fontSize: '0.875rem', 
                color: savingsSummary.totalSaving > 0 ? '#155724' : '#856404', 
                marginTop: '0.5rem',
                margin: 0 
              }}>
                <em>Calculations include upfront costs, running costs, maintenance, and replacement cycles over the analysis period.</em>
              </p>
            </div>
          </section>
        )}

        {/* Carbon Savings Section */}
        {carbonSavingsData?.series.length > 0 && (
          <section className="options-section" style={{ marginTop: '3rem' }}>
            <h2 className="section-title"><span className="emoji-icon">🌱</span> Carbon Emissions Impact</h2>
            <p className="section-intro">
              Projected carbon savings compared to your current system over {carbonSavingsData.metadata.periodYears} years.
            </p>
            <div style={{ marginBottom: '2rem' }}>
              <CarbonSavingsChart data={carbonSavingsData} />
            </div>
            
            {/* Calculate total savings from the primary recommendation */}
            {output.primary?.carbonData && 
             isFinite(output.primary.carbonData.totalSaving_tonnes) &&
             output.primary.carbonData.totalSaving_tonnes > 0 && (
              <div style={{
                background: '#d4edda',
                border: '2px solid #28a745',
                borderRadius: '12px',
                padding: '1.5rem',
                marginTop: '1rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#155724', marginBottom: '0.5rem' }}>
                  Primary Recommendation Carbon Impact
                </h3>
                <p style={{ fontSize: '1rem', color: '#155724', marginBottom: '0.5rem' }}>
                  Total Savings: <strong>{output.primary.carbonData.totalSaving_tonnes.toFixed(1)} tonnes CO₂</strong> over {carbonSavingsData.metadata.periodYears} years
                </p>
                <p style={{ fontSize: '0.875rem', color: '#155724', margin: 0 }}>
                  {!isNaN(output.primary.carbonData.percentReduction) && isFinite(output.primary.carbonData.percentReduction) && (
                    <>This represents a <strong>{output.primary.carbonData.percentReduction.toFixed(0)}% reduction</strong> compared to keeping your current system.{' '}</>
                  )}
                  Calculations use UK grid carbon intensity with projected decarbonization.
                </p>
              </div>
            )}
          </section>
        )}

        <footer className="brief-footer">
          <p><strong>Next Steps:</strong> These options are ranked by overall suitability to your home and priorities.
          We recommend discussing the top 2-3 options with qualified installers to get detailed quotes and technical assessments.
          All systems shown are technically viable for your property.</p>
          
          <div style={{
            marginTop: '2rem',
            paddingTop: '1rem',
            borderTop: '1px solid #dee2e6',
            fontSize: '0.75rem',
            color: '#6c757d',
            lineHeight: '1.6'
          }}>
            <p style={{ margin: '0.5rem 0' }}>
              {IP_NOTICE_MINIMAL} |
              Generated: {new Date().toLocaleDateString('en-GB')} |
              Document ID: {Date.now().toString(36).toUpperCase()}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default DecisionBriefPage;
